import { useState, useCallback } from 'react';
import { query, collection, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { firestore } from '../../../context/FirebaseConfig';
import { getLabelIdMatchingGroup } from '../../../shared_components/utils/FirebaseLabelsetsUtils';

/**
 * Hook to fetch suggested materials based on supplier and field selection
 * This hook provides material suggestions for the ButtonGrid component
 * based on previous deliveries in Firestore
 */
export function useMaterialSuggestions() {
  const [suggestedMaterials, setSuggestedMaterials] = useState({});
  const [isLoading, setIsLoading] = useState({});

  /**
   * Fetches a suggested material for a specific supplier/field combination
   * @param {string} supplierId - The supplier ID in Firestore
   * @param {string} fieldId - The field ID in Firestore
   * @param {string} companyId - The company ID for the client path
   * @param {number|string} index - The index or key to store the result under
   * @returns {Promise<void>}
   */
  const fetchSuggestedMaterial = useCallback(
    async (supplierId, fieldId, companyId, index, globalLabelsets) => {
      if (!supplierId || !fieldId || !companyId || index === undefined) {
        console.warn(
          `Missing parameters for material suggestion: supplierId=${supplierId}, fieldId=${fieldId}, companyId=${companyId}, index=${index}`,
        );
        setIsLoading((prev) => ({ ...prev, [index]: false }));
        return;
      }

      if (isLoading[index]) return;

      setIsLoading((prev) => ({ ...prev, [index]: true }));

      try {
        const clientPath = `clients/${companyId}`;
        const deliveriesRef = collection(firestore, `${clientPath}/deliveries`);

        const materialQuery = query(
          deliveriesRef,
          where('supplier_id', '==', supplierId),
          where('storage_field', '==', fieldId),
          orderBy('date', 'desc'),
          limit(1),
        );

        const querySnapshot = await getDocs(materialQuery);

        if (querySnapshot.empty) {
          console.warn(`No matching documents found for button ${index}`);
        } else {
          const doc = querySnapshot.docs[0];
          const data = doc.data();

          // Access the correct path to material
          const materialId = getLabelIdMatchingGroup(data.delivery_labels, globalLabelsets, 'materials');

          if (!materialId) {
            console.warn(`Material not found in the expected structure for document ${doc.id}`);
          } else {
            // No need to fetch additional material info here
            // The ButtonGrid component already has the materialLabels from props
            setSuggestedMaterials((prev) => {
              if (prev[index]?.material !== materialId) {
                return {
                  ...prev,
                  [index]: {
                    found: true,
                    material: materialId, // This is the key for the material dropdown
                    date: data.date,
                  },
                };
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error(`Error fetching material suggestion for button ${index}:`, error);
      } finally {
        setIsLoading((prev) => ({ ...prev, [index]: false }));
      }
    },
    [isLoading],
  );

  /**
   * Clears a material suggestion for a specific index
   * @param {number|string} index - The index to clear
   */
  const clearSuggestedMaterial = useCallback((index) => {
    setSuggestedMaterials((prev) => {
      if (prev[index]) {
        const newSuggestions = { ...prev };
        delete newSuggestions[index];
        return newSuggestions;
      }
      return prev;
    });

    setIsLoading((prev) => {
      if (prev[index]) {
        const newLoading = { ...prev };
        delete newLoading[index];
        return newLoading;
      }
      return prev;
    });
  }, []);

  /**
   * Clears all material suggestions
   */
  const clearAllSuggestedMaterials = useCallback(() => {
    setSuggestedMaterials({});
    setIsLoading({});
  }, []);

  return {
    suggestedMaterials,
    isLoading,
    fetchSuggestedMaterial,
    clearSuggestedMaterial,
    clearAllSuggestedMaterials,
  };
}
